/**
 * Capacity Service
 * US-013D Phase 2: API calls for capacity management
 */

import supabase from '@/supabase-client'
import { toLocalDateString } from '@/utils/dateUtils'

const FUNCTION_NAME = 'production-forecast'

/**
 * Call the production-forecast Edge Function
 */
async function callForecastFunction(body) {
  const { data, error } = await supabase.functions.invoke(FUNCTION_NAME, {
    body
  })

  if (error) {
    console.error('[CapacityService] Function error:', error)
    throw new Error(error.message || 'Capacity function failed')
  }

  return data
}

// =============================================================================
// Capacity API Functions
// =============================================================================

/**
 * Check available capacity for a specific date
 *
 * @param {Object} options
 * @param {string} [options.date] - Target date (YYYY-MM-DD), default today
 * @param {string} [options.resource_id] - Optional: specific resource
 * @returns {Promise<Object>} Capacity check result
 */
export async function checkCapacity(options = {}) {
  return callForecastFunction({
    action: 'check_capacity',
    date: options.date,
    resource_id: options.resource_id
  })
}

/**
 * Get capacity utilisation for a date range
 *
 * @param {Object} options
 * @param {string} [options.start_date] - Start date (YYYY-MM-DD), default today
 * @param {string} [options.end_date] - End date (YYYY-MM-DD), default today + 13
 * @param {string} [options.resource_id] - Optional: specific resource
 * @returns {Promise<Object>} Utilisation data
 */
export async function getUtilisation(options = {}) {
  return callForecastFunction({
    action: 'get_utilisation',
    start_date: options.start_date,
    end_date: options.end_date,
    resource_id: options.resource_id
  })
}

/**
 * Create a resource allocation
 *
 * @param {Object} options
 * @param {string} options.resource_id - Resource ID
 * @param {string} options.date - Date (YYYY-MM-DD)
 * @param {string} options.entity_type - Type of entity
 * @param {string} options.entity_id - Entity ID
 * @param {number} options.quantity - Quantity to allocate
 * @param {string} [options.allocation_type='planned'] - 'planned' or 'actual'
 * @returns {Promise<Object>} Allocation result
 */
export async function allocateResource(options) {
  return callForecastFunction({
    action: 'allocate',
    resource_id: options.resource_id,
    date: options.date,
    entity_type: options.entity_type,
    entity_id: options.entity_id,
    quantity: options.quantity,
    allocation_type: options.allocation_type || 'planned'
  })
}

/**
 * Remove a resource allocation
 *
 * @param {string} allocationId - Allocation ID to remove
 * @returns {Promise<Object>} Deallocation result
 */
export async function deallocateResource(allocationId) {
  return callForecastFunction({
    action: 'deallocate',
    allocation_id: allocationId
  })
}

// =============================================================================
// Conflict API Functions
// =============================================================================

/**
 * Detect capacity conflicts in a date range
 *
 * @param {Object} options
 * @param {string} [options.start_date] - Start date (YYYY-MM-DD)
 * @param {string} [options.end_date] - End date (YYYY-MM-DD)
 * @returns {Promise<Object>} Conflicts data
 */
export async function detectConflicts(options = {}) {
  return callForecastFunction({
    action: 'detect_conflicts',
    start_date: options.start_date,
    end_date: options.end_date
  })
}

/**
 * Get resolution suggestions for a conflict
 *
 * @param {string} date - Conflict date (YYYY-MM-DD)
 * @param {string} resourceId - Resource ID with conflict
 * @returns {Promise<Object>} Resolution suggestions
 */
export async function resolveConflict(date, resourceId) {
  return callForecastFunction({
    action: 'resolve_conflict',
    date,
    resource_id: resourceId
  })
}

/**
 * Log equipment downtime
 *
 * @param {Object} options
 * @param {string} options.resource_id - Resource ID
 * @param {string} options.start_date - Start date (YYYY-MM-DD)
 * @param {string} [options.end_date] - End date (YYYY-MM-DD), null for ongoing
 * @param {string} options.reason - Reason for downtime
 * @param {string} [options.notes] - Additional notes
 * @returns {Promise<Object>} Downtime record result
 */
export async function logDowntime(options) {
  return callForecastFunction({
    action: 'log_downtime',
    resource_id: options.resource_id,
    start_date: options.start_date,
    end_date: options.end_date,
    reason: options.reason,
    notes: options.notes
  })
}

/**
 * Assess the impact of a downtime period
 *
 * @param {string} downtimeId - Downtime record ID
 * @returns {Promise<Object>} Impact assessment
 */
export async function assessDowntimeImpact(downtimeId) {
  return callForecastFunction({
    action: 'assess_downtime_impact',
    downtime_id: downtimeId
  })
}

// =============================================================================
// Direct Database Queries (for UI lookups)
// =============================================================================

/**
 * Get list of all active resources
 *
 * @returns {Promise<Array>} List of resources
 */
export async function getResources() {
  const { data, error } = await supabase
    .from('production_resources')
    .select('*')
    .eq('is_active', true)
    .order('code')

  if (error) {
    console.error('[CapacityService] Resources query error:', error)
    throw new Error(error.message || 'Failed to fetch resources')
  }

  return data || []
}

/**
 * Get current/upcoming downtime records
 *
 * @param {Object} options
 * @param {string} [options.resource_id] - Filter by resource
 * @returns {Promise<Array>} List of downtime records
 */
export async function getDowntimes(options = {}) {
  const today = toLocalDateString(new Date())

  let query = supabase
    .from('resource_downtime')
    .select(`
      *,
      production_resources (
        code,
        name
      )
    `)
    .or(`end_date.gte.${today},end_date.is.null`)
    .order('start_date')

  if (options.resource_id) {
    query = query.eq('resource_id', options.resource_id)
  }

  const { data, error } = await query

  if (error) {
    console.error('[CapacityService] Downtimes query error:', error)
    throw new Error(error.message || 'Failed to fetch downtimes')
  }

  return data || []
}

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Get utilisation status color based on percentage
 *
 * @param {number} pct - Utilisation percentage
 * @returns {string} Tailwind color class
 */
export function getUtilisationColor(pct) {
  if (pct > 100) return 'bg-red-500'      // Over-capacity
  if (pct >= 90) return 'bg-amber-500'    // Tight
  if (pct >= 60) return 'bg-emerald-500'  // Normal
  return 'bg-gray-400'                     // Underutilised
}

/**
 * Get utilisation status label
 *
 * @param {number} pct - Utilisation percentage
 * @returns {string} Status label
 */
export function getUtilisationLabel(pct) {
  if (pct > 100) return 'Over-capacity'
  if (pct >= 90) return 'Tight'
  if (pct >= 60) return 'Normal'
  return 'Low'
}

/**
 * Format date for display (UK format)
 *
 * @param {string} dateStr - YYYY-MM-DD date string
 * @returns {string} Formatted date
 */
export function formatDateShort(dateStr) {
  if (!dateStr) return '-'
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short'
  })
}

export default {
  // Capacity
  checkCapacity,
  getUtilisation,
  allocateResource,
  deallocateResource,
  // Conflicts
  detectConflicts,
  resolveConflict,
  logDowntime,
  assessDowntimeImpact,
  // Database
  getResources,
  getDowntimes,
  // Utilities
  getUtilisationColor,
  getUtilisationLabel,
  formatDateShort
}
